// Goal-Oriented Roadmap
// Now-Next-Later Kanban Board using @hello-pangea/dnd

import { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, MoreVertical, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRoadmap } from '../shared/RoadmapContext';
import { gnlApi } from '../shared/api';
import { STATUS_COLORS } from '../shared/constants';
import type { GnlColumn, GnlItem, Priority, RoadmapStatus } from '../shared/types';
import { toast } from 'sonner';
import { useIsNarrowViewport } from '@/hooks/useIsNarrowViewport';
import { ViewLayout } from '../shared/ViewLayout';

export function GoalOrientedRoadmap() {
    const { user } = useAuth();
    const { roadmap } = useRoadmap();
    // H5 (audit): switch to stacked layout for tablet-and-below, not just phones.
    // The desktop horizontal Kanban needs at least ~1024 px to fit 5 columns comfortably.
    const isMobile = useIsNarrowViewport();
    const [columns, setColumns] = useState<GnlColumn[]>([]);
    const [items, setItems] = useState<GnlItem[]>([]);
    const [loading, setLoading] = useState(true);

    // UI State
    const [newItemValues, setNewItemValues] = useState<Record<string, string>>({});
    const [addingItemTo, setAddingItemTo] = useState<string | null>(null);
    const [editingItem, setEditingItem] = useState<GnlItem | null>(null);

    // Mobile: track which columns are expanded
    const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
    // GNL-3: WIP limit dialog state
    const [wipDialog, setWipDialog] = useState<{ columnId: string; currentLimit: number } | null>(null);
    const [wipInput, setWipInput] = useState('');
    // GNL-10: Delete confirmation state
    const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
    // GNL-7: Delete column confirmation state
    const [deleteColumnId, setDeleteColumnId] = useState<string | null>(null);
    // GNL-9: Track original column of item being edited so we can detect a move on save
    const [editingItemOriginalColumn, setEditingItemOriginalColumn] = useState<string | null>(null);

    // Load data
    const loadData = useCallback(async () => {
        if (!user || !roadmap) return;
        setLoading(true);
        try {
            const { data, error } = await gnlApi.getBoard(roadmap.id);

            if (error) {
                console.error('Failed to load board:', error);
                return;
            }

            const { columns: cols, items: its } = data || {};

            if (!cols || cols.error) {
                // Ignore AbortErrors (expected during navigation/cleanup)
                const isAbortError = cols?.error?.message?.includes('abort') || cols?.error?.name === 'AbortError';
                if (isAbortError) {
                    setLoading(false);
                    return;
                }
                console.error('Failed to load columns:', cols?.error);
                return;
            }

            // If no columns, initialize default ones
            if (!cols.data || cols.data.length === 0) {
                console.log('Initializing columns for user:', user.id);
                const { error: initError } = await gnlApi.initializeColumns(user.id, roadmap.id);
                if (initError) {
                    console.error('Failed to initialize columns:', initError);
                    toast.error('Failed to initialize board');
                }

                const { data: retryData } = await gnlApi.getBoard(roadmap.id);
                if (retryData?.columns.data) setColumns(retryData.columns.data);
                if (retryData?.items.data) setItems(retryData.items.data);
            } else {
                setColumns(cols.data);
                setItems(its.data || []);
            }
        } catch (error: unknown) {
            // Silently ignore abort errors (expected during cleanup)
            if (error instanceof Error && (error.message?.includes('abort') || error.name === 'AbortError')) {
                return;
            }
            console.error('Failed to load board:', error);
            toast.error('Failed to load board');
        } finally {
            setLoading(false);
        }
    }, [user, roadmap]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // GNL-2: When columns first load, expand all of them on mobile
    useEffect(() => {
        if (columns.length > 0 && expandedColumns.size === 0) {
            setExpandedColumns(new Set(columns.map(c => c.id)));
        }
    }, [columns, expandedColumns.size]);

    const getItemsByColumn = (columnId: string) => {
        return items
            .filter((i) => i.column_id === columnId)
            .sort((a, b) => a.order_index - b.order_index);
    };

    // --- Actions ---

    const handleDragEnd = async (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) return;

        // Optimistic update
        const newItems = [...items];
        const movedItem = newItems.find(i => i.id === draggableId);
        if (!movedItem) return;

        if (source.droppableId === destination.droppableId) {
            // GNL-4: Within-column reorder — splice to new position and refresh order_index
            const colItems = newItems
                .filter(i => i.column_id === source.droppableId)
                .sort((a, b) => a.order_index - b.order_index);
            const fromIdx = colItems.findIndex(i => i.id === draggableId);
            colItems.splice(fromIdx, 1);
            colItems.splice(destination.index, 0, movedItem);
            colItems.forEach((item, idx) => { item.order_index = idx; });
        } else {
            movedItem.column_id = destination.droppableId;
        }

        setItems(newItems);

        // Persist
        await gnlApi.moveItem(draggableId, destination.droppableId, destination.index);
        loadData(); // Sync with server
    };

    const handleAddItem = async (columnId: string) => {
        const content = newItemValues[columnId];
        if (!content?.trim()) return;

        // GNL-8: Block when at WIP limit
        const column = columns.find(c => c.id === columnId);
        if (column?.wip_limit && getItemsByColumn(columnId).length >= column.wip_limit) {
            toast.error(`WIP limit reached (max ${column.wip_limit}). Move or remove items first.`);
            return;
        }

        const { error } = await gnlApi.createItem({ column_id: columnId, content });
        if (error) {
            toast.error('Failed to add item');
            return;
        }

        setNewItemValues((prev) => ({ ...prev, [columnId]: '' }));
        setAddingItemTo(null);
        loadData();
        toast.success('Item added');
    };







    const handleSetWipLimit = async (id: string, limit: number | null) => {
        const finalLimit = limit && limit > 0 ? limit : undefined;
        await gnlApi.updateColumn(id, { wip_limit: finalLimit });
        loadData();
    };

    const handleOpenWipDialog = (columnId: string, currentLimit?: number) => {
        setWipInput(currentLimit ? String(currentLimit) : '');
        setWipDialog({ columnId, currentLimit: currentLimit || 0 });
    };

    const handleConfirmWipLimit = async () => {
        if (!wipDialog) return;
        const parsed = parseInt(wipInput);
        await handleSetWipLimit(wipDialog.columnId, isNaN(parsed) ? null : parsed);
        setWipDialog(null);
        setWipInput('');
    };

    const handleDeleteItem = async (id: string) => {
        const { error } = await gnlApi.deleteItem(id);
        if (error) {
            toast.error('Failed to delete item');
            return;
        }
        if (editingItem?.id === id) setEditingItem(null);
        loadData();
        toast.success('Item deleted');
    };

    // GNL-9: Open the edit dialog and remember the original column so we can move on save
    const openEditDialog = (item: GnlItem) => {
        setEditingItem(item);
        setEditingItemOriginalColumn(item.column_id);
    };

    // GNL-7: Delete a column (cascade removes its items at the DB level via FK / RLS)
    const handleDeleteColumn = async (id: string) => {
        const { error } = await gnlApi.deleteColumn(id);
        if (error) {
            toast.error('Failed to delete column');
            return;
        }
        loadData();
        toast.success('Column deleted');
    };

    const handleUpdateItem = async (id: string, updates: Partial<GnlItem>) => {
        const { error } = await gnlApi.updateItem(id, updates);
        if (error) {
            toast.error('Failed to update item');
            return;
        }
        // Update local state if editing
        if (editingItem?.id === id) {
            setEditingItem(prev => prev ? { ...prev, ...updates } : null);
        }
        loadData();
        toast.success('Item updated');
    };

    return (
        <ViewLayout title="Goal Board" isLoading={loading} loadingText="Loading roadmap..." fullHeight>
            {/* Board Area */}
            <DragDropContext onDragEnd={handleDragEnd}>
                {/* Mobile: Stacked collapsible columns */}
                {columns.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-muted/10 rounded-xl border-2 border-dashed border-muted/50 transition-colors hover:border-muted-foreground/30">
                        <AlertCircle className="h-10 w-10 text-muted-foreground/30 mb-4" />
                        <h3 className="text-lg font-semibold text-muted-foreground">No columns found</h3>
                        <p className="text-sm text-muted-foreground/60 max-w-xs mt-1">
                            Your roadmap is ready, but doesn't have any stages yet.
                        </p>
                    </div>
                ) : isMobile ? (
                    <div className="flex-1 overflow-y-auto space-y-3 pb-4">
                        {columns.map((column) => {
                            const columnItems = getItemsByColumn(column.id);
                            const isAdding = addingItemTo === column.id;
                            const isOverLimit = column.wip_limit && columnItems.length > column.wip_limit;
                            const isExpanded = expandedColumns.has(column.id);

                            const toggleExpanded = () => {
                                setExpandedColumns(prev => {
                                    const updated = new Set(prev);
                                    if (updated.has(column.id)) {
                                        updated.delete(column.id);
                                    } else {
                                        updated.add(column.id);
                                    }
                                    return updated;
                                });
                            };

                            return (
                                <Collapsible
                                    key={column.id}
                                    open={isExpanded}
                                    onOpenChange={toggleExpanded}
                                    className="w-full rounded-xl bg-muted/30 border border-border/60 overflow-hidden shadow-sm"
                                >
                                    {/* Mobile Column Header - touch-friendly 44px height */}
                                    <CollapsibleTrigger asChild>
                                        <button className="w-full min-h-[44px] p-3 border-b bg-muted/40 flex items-center justify-between active:bg-muted/60 transition-colors group/header">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                {isExpanded ? (
                                                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                )}
                                                <h3 className="font-semibold text-sm truncate" title={column.title}>
                                                    {column.title}
                                                </h3>
                                                <Badge variant={isOverLimit ? "destructive" : "secondary"} className="h-5 px-1.5 text-[10px]">
                                                    {columnItems.length}{column.wip_limit ? `/${column.wip_limit}` : ''}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-9 w-9"
                                                    onClick={(e) => { e.stopPropagation(); setAddingItemTo(column.id); }}
                                                    aria-label="Add item"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-9 w-9">
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleOpenWipDialog(column.id, column.wip_limit)}>
                                                            Set WIP Limit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            className="text-destructive focus:text-destructive"
                                                            onClick={(e) => { e.stopPropagation(); setDeleteColumnId(column.id); }}
                                                        >
                                                            Delete Column
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </button>
                                    </CollapsibleTrigger>

                                    {/* WIP Warning */}
                                    {isOverLimit && (
                                        <div className="bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive flex items-center justify-center border-b border-destructive/20">
                                            <AlertCircle className="h-3 w-3 mr-1.5" /> WIP Limit Exceeded
                                        </div>
                                    )}

                                    <CollapsibleContent>
                                        <Droppable droppableId={column.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`p-3 transition-colors ${snapshot.isDraggingOver ? 'bg-primary/5' : ''}`}
                                                >
                                                    {/* Add Item Form */}
                                                    {isAdding && (
                                                        <div className="mb-3 p-3 bg-card rounded-lg border shadow-sm animate-in fade-in">
                                                            <Input
                                                                placeholder={`Add to ${column.title}...`}
                                                                className="mb-2 h-11"
                                                                autoFocus
                                                                value={newItemValues[column.id] || ''}
                                                                onChange={(e) => setNewItemValues(prev => ({ ...prev, [column.id]: e.target.value }))}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleAddItem(column.id);
                                                                    if (e.key === 'Escape') setAddingItemTo(null);
                                                                }}
                                                            />
                                                            <div className="flex justify-end gap-2 pt-2 border-t mt-1">
                                                                <Button size="sm" variant="ghost" className="h-9 text-sm px-3" onClick={() => setAddingItemTo(null)}>
                                                                    Cancel
                                                                </Button>
                                                                <Button size="sm" className="h-9 text-sm px-3" onClick={() => handleAddItem(column.id)}>
                                                                    Add
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="space-y-3">
                                                        {/* Empty State */}
                                                        {columnItems.length === 0 && !isAdding && (
                                                            <div className="py-8 px-4 text-center border-2 border-dashed border-muted/50 rounded-lg">
                                                                <div className="text-muted-foreground/40 text-sm font-medium">{column.title} is empty</div>
                                                                <Button
                                                                    variant="link"
                                                                    className="text-muted-foreground/50 text-sm h-auto p-0 mt-1"
                                                                    onClick={() => setAddingItemTo(column.id)}
                                                                >
                                                                    + Add item
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {/* Items - larger touch targets for mobile */}
                                                        {columnItems.map((item, index) => (
                                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <Card
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`group relative p-4 bg-card active:bg-card/90 border-border/50 shadow-sm transition-all duration-200 cursor-grab active:cursor-grabbing ${snapshot.isDragging ? 'shadow-xl ring-2 ring-primary/20 z-50' : ''
                                                                            }`}
                                                                        onClick={(e) => {
                                                                            if ((e.target as HTMLElement).closest('button')) return;
                                                                            openEditDialog(item);
                                                                        }}
                                                                    >
                                                                        <div className="flex flex-col gap-2">
                                                                            <div className="flex justify-between items-start gap-2">
                                                                                <p className="text-sm font-medium leading-tight text-card-foreground">
                                                                                    {item.content}
                                                                                </p>
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger asChild>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-9 w-9 -mr-2 -mt-2 shrink-0"
                                                                                        >
                                                                                            <MoreVertical className="h-4 w-4 text-muted-foreground" />
                                                                                        </Button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent align="end" className="w-32">
                                                                                        <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                                                                            Edit
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuSeparator />
                                                                                        <DropdownMenuItem
                                                                                            className="text-destructive focus:text-destructive"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setDeleteItemId(item.id);
                                                                                            }}
                                                                                        >
                                                                                            Delete
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            </div>

                                                                            <div className="flex flex-col gap-2 mt-2 pt-2 border-t text-xs text-muted-foreground">
                                                                                {/* Status Row (GNL-6) */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Status</span>
                                                                                    <div className="flex items-center gap-1">
                                                                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.status] ?? '#9ca3af' }} />
                                                                                        <span className="capitalize">{item.status?.replace('_', ' ') ?? '—'}</span>
                                                                                    </div>
                                                                                </div>
                                                                                {/* Impact Score Row */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Impact score</span>
                                                                                    <Badge
                                                                                        variant="outline"
                                                                                        className="rounded-sm px-1.5 h-5 font-medium border-0"
                                                                                        style={{
                                                                                            backgroundColor: (item.impact_score ?? 0) >= 75 ? '#fef3c7' : (item.impact_score ?? 0) >= 50 ? '#ffedd5' : '#f3f4f6',
                                                                                            color: (item.impact_score ?? 0) >= 75 ? '#d97706' : (item.impact_score ?? 0) >= 50 ? '#ea580c' : '#6b7280'
                                                                                        }}
                                                                                    >
                                                                                        {(item.impact_score ?? 0) >= 50 && <span className="mr-0.5">🔥</span>}
                                                                                        {item.impact_score ?? 0}
                                                                                    </Badge>
                                                                                </div>
                                                                                {/* Teams Row */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Teams</span>
                                                                                    <div className="flex gap-1 flex-wrap justify-end">
                                                                                        {(item.teams && item.teams.length > 0) ? (
                                                                                            item.teams.map((team, i) => (
                                                                                                <Badge
                                                                                                    key={i}
                                                                                                    className="rounded-sm text-[10px] px-1.5 h-5 font-medium bg-purple-600 text-white border-0"
                                                                                                >
                                                                                                    {team}
                                                                                                </Badge>
                                                                                            ))
                                                                                        ) : (
                                                                                            <span className="text-muted-foreground/50">—</span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Card>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                </div>
                                            )}
                                        </Droppable>
                                    </CollapsibleContent>
                                </Collapsible>
                            );
                        })}


                    </div>
                ) : (
                    /* Desktop: Horizontal scrolling columns */
                    <div className="flex-1 overflow-x-auto overflow-y-hidden">
                        <div className="flex h-full gap-6 min-w-max pb-4">
                            {columns.map((column) => {
                                const columnItems = getItemsByColumn(column.id);
                                const isAdding = addingItemTo === column.id;
                                const isOverLimit = column.wip_limit && columnItems.length > column.wip_limit;

                                return (
                                    <div key={column.id} className="flex flex-col w-80 h-full rounded-xl bg-muted/30 border border-border/60 overflow-hidden shadow-sm">
                                        {/* Column Header */}
                                        <div className="p-3 border-b bg-muted/40 flex items-center justify-between group/header">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <h3 className="font-semibold text-sm truncate" title={column.title}>
                                                    {column.title}
                                                </h3>
                                                <Badge variant={isOverLimit ? "destructive" : "secondary"} className="h-5 px-1.5 text-[10px]">
                                                    {columnItems.length}{column.wip_limit ? `/${column.wip_limit}` : ''}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6 opacity-0 group-hover/header:opacity-100 transition-opacity"
                                                    onClick={() => setAddingItemTo(column.id)}
                                                    aria-label="Add item"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                </Button>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover/header:opacity-100 transition-opacity">
                                                            <MoreVertical className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleOpenWipDialog(column.id, column.wip_limit)}>
                                                            Set WIP Limit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            className="text-destructive focus:text-destructive"
                                                            onClick={(e) => { e.stopPropagation(); setDeleteColumnId(column.id); }}
                                                        >
                                                            Delete Column
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>

                                        {/* Messages / Warnings */}
                                        {isOverLimit && (
                                            <div className="bg-destructive/10 px-3 py-1.5 text-[10px] font-medium text-destructive flex items-center justify-center border-b border-destructive/20">
                                                <AlertCircle className="h-3 w-3 mr-1.5" /> WIP Limit Exceeded
                                            </div>
                                        )}

                                        {/* Droppable Area */}
                                        <Droppable droppableId={column.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`flex-1 p-3 overflow-y-auto transition-colors scrollbar-thin scrollbar-thumb-muted-foreground/10 ${snapshot.isDraggingOver ? 'bg-primary/5' : ''
                                                        }`}
                                                >
                                                    {/* Add Item Form */}
                                                    {isAdding && (
                                                        <div className="mb-3 p-3 bg-card rounded-lg border shadow-sm animate-in fade-in slide-in-from-top-1">
                                                            <Input
                                                                placeholder={`Add to ${column.title}...`}
                                                                className="mb-2 border-0 shadow-none focus-visible:ring-0 px-0 h-auto py-1 font-medium bg-transparent placeholder:font-normal"
                                                                autoFocus
                                                                value={newItemValues[column.id] || ''}
                                                                onChange={(e) => setNewItemValues(prev => ({ ...prev, [column.id]: e.target.value }))}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleAddItem(column.id);
                                                                    if (e.key === 'Escape') setAddingItemTo(null);
                                                                }}
                                                            />
                                                            <div className="flex justify-end gap-2 pt-2 border-t mt-1">
                                                                <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setAddingItemTo(null)}>
                                                                    Cancel
                                                                </Button>
                                                                <Button size="sm" className="h-6 text-xs px-2" onClick={() => handleAddItem(column.id)}>
                                                                    Add
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="space-y-3">
                                                        {/* Empty State */}
                                                        {columnItems.length === 0 && !isAdding && (
                                                            <div className="py-12 px-4 text-center border-2 border-dashed border-muted/50 rounded-lg group/empty hover:border-muted-foreground/30 transition-colors">
                                                                <div className="text-muted-foreground/40 text-sm font-medium">{column.title} is empty</div>
                                                                <Button
                                                                    variant="link"
                                                                    className="text-muted-foreground/50 text-xs h-auto p-0 mt-1 group-hover/empty:text-primary transition-colors"
                                                                    onClick={() => setAddingItemTo(column.id)}
                                                                >
                                                                    + Add item
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {/* Items */}
                                                        {columnItems.map((item, index) => (
                                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <Card
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`group relative p-3 bg-card hover:bg-card/80 border-border/50 hover:border-border shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing ${snapshot.isDragging ? 'rotate-2 shadow-xl ring-2 ring-primary/20 z-50 opacity-90' : ''
                                                                            }`}
                                                                        onClick={(e) => {
                                                                            // Prevent clicking if interacting with controls
                                                                            if ((e.target as HTMLElement).closest('button')) return;
                                                                            openEditDialog(item);
                                                                        }}
                                                                    >
                                                                        <div className="flex flex-col gap-2">
                                                                            <div className="flex justify-between items-start gap-2">
                                                                                <p className="text-sm font-medium leading-tight text-card-foreground line-clamp-3">
                                                                                    {item.content}
                                                                                </p>

                                                                                {/* Three dots menu */}
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger asChild>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-5 w-5 -mr-1 -mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                        >
                                                                                            <MoreVertical className="h-3 w-3 text-muted-foreground" />
                                                                                        </Button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent align="end" className="w-32">
                                                                                        <DropdownMenuItem onClick={() => openEditDialog(item)}>
                                                                                            Edit
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuSeparator />
                                                                                        <DropdownMenuItem
                                                                                            className="text-destructive focus:text-destructive"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setDeleteItemId(item.id);
                                                                                            }}
                                                                                        >
                                                                                            Delete
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            </div>

                                                                            <div className="flex flex-col gap-2 mt-2 pt-2 border-t text-xs text-muted-foreground">
                                                                                {/* Status Row (GNL-6) */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Status</span>
                                                                                    <div className="flex items-center gap-1">
                                                                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.status] ?? '#9ca3af' }} />
                                                                                        <span className="capitalize">{item.status?.replace('_', ' ') ?? '—'}</span>
                                                                                    </div>
                                                                                </div>
                                                                                {/* Impact Score Row */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Impact score</span>
                                                                                    <Badge
                                                                                        variant="outline"
                                                                                        className="rounded-sm px-1.5 h-5 font-medium border-0"
                                                                                        style={{
                                                                                            backgroundColor: (item.impact_score ?? 0) >= 75 ? '#fef3c7' : (item.impact_score ?? 0) >= 50 ? '#ffedd5' : '#f3f4f6',
                                                                                            color: (item.impact_score ?? 0) >= 75 ? '#d97706' : (item.impact_score ?? 0) >= 50 ? '#ea580c' : '#6b7280'
                                                                                        }}
                                                                                    >
                                                                                        {(item.impact_score ?? 0) >= 50 && <span className="mr-0.5">🔥</span>}
                                                                                        {item.impact_score ?? 0}
                                                                                    </Badge>
                                                                                </div>
                                                                                {/* Teams Row */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <span>Teams</span>
                                                                                    <div className="flex gap-1 flex-wrap justify-end">
                                                                                        {(item.teams && item.teams.length > 0) ? (
                                                                                            item.teams.map((team, i) => (
                                                                                                <Badge
                                                                                                    key={i}
                                                                                                    className="rounded-sm text-[10px] px-1.5 h-5 font-medium bg-purple-600 text-white border-0"
                                                                                                >
                                                                                                    {team}
                                                                                                </Badge>
                                                                                            ))
                                                                                        ) : (
                                                                                            <span className="text-muted-foreground/50">—</span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Card>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                );
                            })}

                            {/* Add Column Button (at end of list) */}

                        </div>
                    </div>
                )}
            </DragDropContext>

            {/* Item Detail Dialog */}
            <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) { setEditingItem(null); setEditingItemOriginalColumn(null); } }}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Edit Item</DialogTitle>
                    </DialogHeader>
                    {editingItem && (
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Content</label>
                                <Input
                                    value={editingItem.content}
                                    onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Description</label>
                                <Textarea
                                    value={editingItem.description || ''}
                                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                    placeholder="Add more details..."
                                    className="min-h-[100px]"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Impact Score (0-100)</label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={editingItem.impact_score ?? 0}
                                    onChange={(e) => setEditingItem({ ...editingItem, impact_score: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Teams (comma-separated)</label>
                                <Input
                                    value={(editingItem.teams || []).join(', ')}
                                    onChange={(e) => setEditingItem({ ...editingItem, teams: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                    placeholder="e.g. PD dept., Engineering"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Priority</label>
                                <Select
                                    value={editingItem.priority}
                                    onValueChange={(val: Priority) => setEditingItem({ ...editingItem, priority: val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select priority" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="high">High</SelectItem>
                                        <SelectItem value="critical">Critical</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* GNL-6: Status field */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Status</label>
                                <Select
                                    value={editingItem.status}
                                    onValueChange={(val: RoadmapStatus) => setEditingItem({ ...editingItem, status: val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="planned">Planned</SelectItem>
                                        <SelectItem value="in_progress">In Progress</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="blocked">Blocked</SelectItem>
                                        <SelectItem value="deferred">Deferred</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* GNL-9: Column selector — keyboard-accessible alternative to drag-and-drop */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Column</label>
                                <Select
                                    value={editingItem.column_id}
                                    onValueChange={(val: string) => setEditingItem({ ...editingItem, column_id: val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select column" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {columns.map((col) => (
                                            <SelectItem key={col.id} value={col.id}>{col.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="destructive"
                            type="button"
                            className="mr-auto"
                            onClick={() => {
                                if (editingItem) { setDeleteItemId(editingItem.id); setEditingItem(null); }
                            }}
                        >
                            Delete
                        </Button>
                        <Button variant="outline" onClick={() => { setEditingItem(null); setEditingItemOriginalColumn(null); }}>Cancel</Button>
                        <Button onClick={async () => {
                            if (!editingItem) return;
                            // GNL-9: if the column changed via the column selector, move the item first
                            const movedColumn =
                                editingItemOriginalColumn !== null &&
                                editingItem.column_id !== editingItemOriginalColumn;
                            if (movedColumn) {
                                const destItems = items.filter(i => i.column_id === editingItem.column_id);
                                const newOrderIndex = destItems.length;
                                const { error } = await gnlApi.moveItem(editingItem.id, editingItem.column_id, newOrderIndex);
                                if (error) {
                                    toast.error('Failed to move item');
                                    return;
                                }
                            }
                            await handleUpdateItem(editingItem.id, {
                                content: editingItem.content,
                                description: editingItem.description,
                                priority: editingItem.priority,
                                impact_score: editingItem.impact_score,
                                teams: editingItem.teams,
                                status: editingItem.status,
                            });
                            setEditingItem(null);
                            setEditingItemOriginalColumn(null);
                        }}>
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* GNL-10: Delete item confirmation */}
            <AlertDialog open={!!deleteItemId} onOpenChange={(open) => !open && setDeleteItemId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Item?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the item from the board.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { if (deleteItemId) handleDeleteItem(deleteItemId); setDeleteItemId(null); }}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* GNL-7: Delete column confirmation */}
            <AlertDialog open={!!deleteColumnId} onOpenChange={(open) => !open && setDeleteColumnId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Column?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the column and all items in it. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { if (deleteColumnId) handleDeleteColumn(deleteColumnId); setDeleteColumnId(null); }}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* GNL-3: WIP Limit Dialog (replaces window.prompt) */}
            <Dialog open={!!wipDialog} onOpenChange={(open) => !open && setWipDialog(null)}>
                <DialogContent className="sm:max-w-xs">
                    <DialogHeader>
                        <DialogTitle>Set WIP Limit</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Enter the maximum number of items allowed in this column (0 or blank for no limit).
                        </p>
                        <Input
                            type="number"
                            min={0}
                            placeholder="e.g. 5"
                            value={wipInput}
                            onChange={(e) => setWipInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleConfirmWipLimit()}
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setWipDialog(null)}>Cancel</Button>
                        <Button onClick={handleConfirmWipLimit}>Apply</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </ViewLayout>
    );
}

export default GoalOrientedRoadmap;
